import { SYSTEM_FINANCING_MODE_CODE } from "~/utils/enums/financing-modes.enums";

/**
 * Canonical display name for a financing mode. Used on every surface
 * (settings list, cost tab, budget tab, session recap, mode pickers) so the
 * label never drifts between them.
 *
 * Why this exists — the modes kept by default arrive from the API lowercased
 * ("budget" / "opco" / "cpf" / "fne"). Product wants:
 *   - the system mode shown as "Budget Entreprise" everywhere (its API name is
 *     the lowercase legacy "budget"),
 *   - the well-known financing acronyms fully uppercased,
 *   - any other (admin-created) mode shown as typed, with a capitalised first
 *     letter as a safety net for stray lowercase names.
 *
 * NOTE: the acronym map is a front-end normalisation of legacy seed data. The
 * durable fix is to store proper names on the backend seed — at which point this
 * collapses to "system → Budget Entreprise, else the name as stored".
 */
const SYSTEM_MODE_LABEL = "Budget Entreprise";

const ACRONYM_LABELS: Record<string, string> = {
  opco: "OPCO",
  cpf: "CPF",
  fne: "FNE",
};

export const displayFinancingModeName = (mode: {
  name?: string | null;
  code?: string | null;
  isSystem?: boolean;
}): string => {
  if (mode.isSystem || mode.code === SYSTEM_FINANCING_MODE_CODE) return SYSTEM_MODE_LABEL;

  const name = mode.name?.trim() ?? "";
  if (!name) return "";

  return ACRONYM_LABELS[name.toLowerCase()] ?? name.charAt(0).toUpperCase() + name.slice(1);
};
